import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesCheckSeverity } from "../../../Enums/voices";
import { VoicesPostBlocker, VoicesPostCheck } from "../../../Models/voicesPosts";

interface Props {
  blockers: VoicesPostBlocker[];
  checks: VoicesPostCheck[];
}

const readable = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.map((item) => `"${item}"`).join(", ") : value,
    ]),
  );

const VoicesChecksPanel: React.FC<Props> = ({ blockers, checks }) => {
  const { t } = useTranslation("voicesPosts");

  const blocking = [
    ...blockers.map((blocker) => t(`blocker_${blocker}`)),
    ...checks
      .filter((check) => check.severity === VoicesCheckSeverity.BLOCK)
      .map((check) => t(`check_${check.key}`, readable(check.params))),
  ];
  const warnings = checks
    .filter((check) => check.severity === VoicesCheckSeverity.WARN)
    .map((check) => t(`check_${check.key}`, readable(check.params)));

  const section = (title: string, lines: string[], background: string, border: string) =>
    lines.length > 0 && (
      <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: background, border: `1px solid ${border}` }}>
        <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
          {title}
        </Typography>
        {lines.map((line) => (
          <Typography key={line} variant="body2">
            • {line}
          </Typography>
        ))}
      </Box>
    );

  if (!blocking.length && !warnings.length) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {section(t("blockersTitle"), blocking, color.red50, color.red200)}
      {section(t("warningsTitle"), warnings, color.orange50, color.orange200)}
    </Box>
  );
};

export default VoicesChecksPanel;
